import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import API_BASE_URL from '../config/api';

const FinalSubmissionPage = () => {
  const { school } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  
  // Get data from previous pages
  const { submissionData, photos } = location.state || {};
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('profile'); // profile, photos, complete
  const [submissionId, setSubmissionId] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValidated, setPromoCodeValidated] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [showPromoSection, setShowPromoSection] = useState(false);

  // Fetch school name from database
  useEffect(() => {
    const fetchSchoolName = async () => {
      if (!school) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/schools/${school}`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setSchoolName(result.data.school_name);
        } else {
          setSchoolName(school.toUpperCase());
        }
      } catch (error) {
        console.error('Error fetching school name:', error);
        setSchoolName(school.toUpperCase());
      }
    };

    fetchSchoolName();
  }, [school]);

  // Redirect if missing data
  useEffect(() => {
    if (!submissionData || !photos) {
      navigate(`/submit/${school}`);
    }
  }, [submissionData, photos, navigate, school]);

  const submitProfile = async () => {
    try {
      setCurrentStep('profile');
      setSubmissionProgress(20);

      // Ensure school is included in submission data
      const submissionPayload = {
        ...submissionData,
        school: school // Make sure school from URL is included
      };

      console.log('Submitting profile data:', submissionPayload);

      const response = await fetch(`${API_BASE_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Profile submission failed:', errorData);
        throw new Error(`Profile submission failed: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      console.log('Profile submission successful:', result);
      setSubmissionId(result.data.id);
      setSubmissionProgress(40);
      
      return result.data.id;
    } catch (error) {
      console.error('Profile submission error:', error);
      throw new Error(`Failed to submit profile: ${error.message}`);
    }
  };

  const uploadPhotos = async (profileSubmissionId) => {
    try {
      setCurrentStep('photos');
      setSubmissionProgress(50);

      const photoKeys = [];
      const totalPhotos = photos.length;

      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        
        // Get signed URL from backend
        const signedUrlResponse = await fetch(`${API_BASE_URL}/api/photos/upload-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileName: photo.fileName,
            fileType: 'image/jpeg',
            submissionId: profileSubmissionId,
            school: school,
            instagramHandle: submissionData.instagram_handle || 'nohandle',
            photoIndex: i
          }),
        });

        if (!signedUrlResponse.ok) {
          throw new Error(`Failed to get upload URL for photo ${i + 1}`);
        }

        const { data } = await signedUrlResponse.json();
        
        // Create file from blob
        const file = new File([photo.blob], photo.fileName, { type: 'image/jpeg' });
        
        // Upload to S3
        const uploadResponse = await fetch(data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': 'image/jpeg',
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload photo ${i + 1} to S3`);
        }

        photoKeys.push(data.fileKey);
        
        // Update progress
        const photoProgress = ((i + 1) / totalPhotos) * 40; // Photos are 40% of total progress
        setSubmissionProgress(50 + photoProgress);
      }

      return photoKeys;
    } catch (error) {
      console.error('Photo upload error:', error);
      throw new Error('Failed to upload photos');
    }
  };

  const updateSubmissionWithPhotos = async (profileSubmissionId, photoKeys) => {
    try {
      setSubmissionProgress(95);

      const response = await fetch(`${API_BASE_URL}/api/submissions/${profileSubmissionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photoKeys: photoKeys
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update submission with photos');
      }

      setSubmissionProgress(100);
      return await response.json();
    } catch (error) {
      console.error('Submission update error:', error);
      throw new Error('Failed to link photos to submission');
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }

    setIsValidatingPromo(true);
    setPromoCodeError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promoCode.trim(),
          school: school
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setPromoCodeError(result.message || 'Invalid promo code');
        setPromoCodeValidated(false);
      } else {
        setPromoCodeValidated(true);
        setPromoCodeError('');
        toastRef.current?.show({
          severity: 'success',
          summary: 'Promo Code Valid!',
          detail: `${result.data.remaining_uses} uses remaining`,
          life: 3000
        });
      }
    } catch (error) {
      console.error('Promo code validation error:', error);
      setPromoCodeError('Failed to validate promo code');
      setPromoCodeValidated(false);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const applyPromoCode = async (submissionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promo-codes/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promoCode.trim(),
          school: school,
          submissionId: submissionId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to apply promo code');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Promo code application error:', error);
      throw error;
    }
  };

  const createStripeCheckoutSession = async (submissionId, tierId = 1) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submissionId: submissionId,
          tierId: tierId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Stripe checkout session creation error:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Guard against multiple simultaneous submissions
    if (isSubmitting) {
      console.log('⚠️ Submission already in progress, ignoring duplicate attempt');
      return;
    }
    
    console.log('🚀 Starting final submission process...');
    setIsSubmitting(true);
    
    try {
      // Step 1: Submit profile and get submission ID
      console.log('📝 Submitting profile...');
      const profileSubmissionId = await submitProfile();
      
      // Step 2: Upload photos with real submission ID
      const photoKeys = await uploadPhotos(profileSubmissionId);
      
      // Step 3: Update submission with photo keys
      await updateSubmissionWithPhotos(profileSubmissionId, photoKeys);
      
      setCurrentStep('complete');
      setSubmissionProgress(100);
      
      // Step 4: Check if using promo code or go to tier selection
      if (promoCodeValidated && promoCode.trim()) {
        // Apply promo code - this will give instant free post
        toastRef.current?.show({
          severity: 'success',
          summary: 'Promo Code Applied!',
          detail: 'Your post will be published instantly for free!',
          life: 3000
        });

        const promoResult = await applyPromoCode(profileSubmissionId);
        
        // Skip tier selection - go directly to posting status
        setTimeout(() => {
          navigate(`/posting-status/${profileSubmissionId}`);
        }, 1500);
      } else {
        // Navigate to tier selection page
        toastRef.current?.show({
          severity: 'success',
          summary: 'Submission Complete',
          detail: 'Choose your posting speed...',
          life: 2000
        });

        setTimeout(() => {
          navigate(`/submit/${school}/tier-selection`, {
            state: {
              submissionId: profileSubmissionId,
              submissionData: submissionData,
              photos: photos
            }
          });
        }, 1000);
      }

    } catch (error) {
      console.error('Submission error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Submission Failed',
        detail: error.message || 'Please try again.',
        life: 5000
      });
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/submit/${school}/photos`, { 
      state: { submissionData, photos } 
    });
  };

  if (!submissionData || !photos) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="final-submission-page">
      <Toast ref={toastRef} />
      
      {/* Header */}
      <header className="form-header">
        <div className="header-content">
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="back-button"
            onClick={handleBack}
            disabled={isSubmitting}
          />
          <div className="header-title">
            <p>{schoolName || 'Your University'}</p>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="progress-section">
        <div className="container">
          <div className="progress-info">
            <span>Step 3 of 3: Final Submission</span>
            <span>{Math.round(submissionProgress)}% Complete</span>
          </div>
          <ProgressBar 
            value={submissionProgress} 
            className="form-progress"
            showValue={false}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="final-submission-main">
        <div className="container">
          
          {/* Review Section */}
          <Card className="review-card">
            {/* Profile Summary */}
            <div className="profile-summary">
              <h3>Profile Information</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <label>Name:</label>
                  <span>{submissionData.name}</span>
                </div>
                <div className="summary-item">
                  <label>School:</label>
                  <span>{schoolName}</span>
                </div>
                {submissionData.instagram_handle && (
                  <div className="summary-item">
                    <label>Instagram:</label>
                    <span>
                      {submissionData.instagram_handle.startsWith('@') 
                        ? submissionData.instagram_handle 
                        : `@${submissionData.instagram_handle}`}
                    </span>
                  </div>
                )}
                {submissionData.email && (
                  <div className="summary-item">
                    <label>Email:</label>
                    <span>{submissionData.email}</span>
                  </div>
                )}
                <div className="summary-item full-width">
                  <label>Bio:</label>
                  <span>{submissionData.bio}</span>
                </div>
              </div>
            </div>

            <Divider />

            {/* Photos Summary */}
            <div className="photos-summary">
              <h3>Photos ({photos.length})</h3>
              <div className="photo-preview-grid">
                {photos.map((photo, index) => (
                  <div key={index} className="preview-photo-item">
                    <img src={photo.previewUrl} alt={`Photo ${index + 1}`} />
                    {index === 0 && <div className="cover-badge">Cover</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Submission Status */}
            {isSubmitting && (
              <div className="submission-status">
                <Divider />
                <div className="status-content">
                  <h3>
                    {currentStep === 'profile' && 'Submitting Profile...'}
                    {currentStep === 'photos' && 'Uploading Photos...'}
                    {currentStep === 'complete' && 'Submission Complete!'}
                  </h3>
                  <ProgressBar 
                    value={submissionProgress} 
                    showValue={true}
                    className="submission-progress"
                  />
                </div>
              </div>
            )}

            {/* Promo Code Section */}
            <Divider />
            
            <div className="promo-code-section">
              <div className="promo-header">
                <h3>Have a Promo Code?</h3>
                <Button
                  label={showPromoSection ? 'Hide' : 'Enter Code'}
                  className="promo-toggle-button"
                  onClick={() => setShowPromoSection(!showPromoSection)}
                  disabled={isSubmitting}
                />
              </div>
              
              {showPromoSection && (
                <div className="promo-input-section">
                  <div className="promo-input-group">
                    <InputText
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoCodeValidated(false);
                        setPromoCodeError('');
                      }}
                      placeholder="Enter promo code"
                      disabled={isSubmitting || promoCodeValidated}
                      className={`promo-input ${promoCodeError ? 'p-invalid' : ''} ${promoCodeValidated ? 'p-valid' : ''}`}
                    />
                    <Button
                      label={promoCodeValidated ? 'Valid!' : 'Validate'}
                      icon={promoCodeValidated ? 'pi pi-check' : 'pi pi-arrow-right'}
                      onClick={validatePromoCode}
                      disabled={isSubmitting || isValidatingPromo || promoCodeValidated || !promoCode.trim()}
                      loading={isValidatingPromo}
                      className={promoCodeValidated ? 'p-button-success' : ''}
                    />
                  </div>
                  
                  {promoCodeError && (
                    <small className="p-error">{promoCodeError}</small>
                  )}
                  
                  {promoCodeValidated && (
                    <div className="promo-success-message">
                      <i className="pi pi-check-circle"></i>
                      <span>Promo code applied! You can submit for free.</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="final-actions">
              <Button
                label={isSubmitting ? 'Processing...' : (promoCodeValidated ? 'Continue (Free with Promo)' : 'Continue to Tier Selection')}
                icon={isSubmitting ? 'pi pi-spin pi-spinner' : (promoCodeValidated ? 'pi pi-check' : 'pi pi-arrow-right')}
                iconPos="right"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`final-submit-button ${promoCodeValidated ? 'promo-active' : ''}`}
              />
              <p className="final-note">
                {promoCodeValidated 
                  ? 'Your post will be published instantly for free with your promo code!'
                  : "Next, you'll choose your posting speed and complete payment."
                }
              </p>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default FinalSubmissionPage; 